import type { Context, InputValue } from '../../contract/index.js';
import { LoaderError } from '../containment.js';

/** Matches the registry's URL limit. */
export const SRC_MAX_LENGTH = 2048;

/** The integration kind named in a refusal message. */
export type SrcLabel = 'iframe' | 'script';

const PLACEHOLDER = /\{([A-Za-z][A-Za-z0-9_]{0,63})\}/g;
const ANY_PLACEHOLDER = /\{[A-Za-z][A-Za-z0-9_]{0,63}\}/;
const WHOLE_PLACEHOLDER = /^\{[A-Za-z][A-Za-z0-9_]{0,63}\}$/;

/** RFC 3986 unreserved characters, which encoding leaves as they are. */
const UNRESERVED = /^[A-Za-z0-9._~-]+$/;

export interface BuildSrcOptions {
  /**
   * The entry's `hostServed` opt-in. A value filling a path placeholder must
   * then be unreserved characters only, so no input can reach another path on
   * the host through an encoded slash or backslash.
   */
  readonly hostServed?: boolean;
}

function templatePath(template: string): string {
  const afterScheme = template.slice(template.indexOf('://') + 3);
  const start = afterScheme.search(/[/?#]/);
  if (start === -1 || afterScheme[start] !== '/') {
    return '';
  }
  return afterScheme.slice(start).split(/[?#]/, 1)[0] ?? '';
}

/**
 * Why a host-served `src` template is refused, or undefined when its path
 * starts and ends with a literal segment, has no empty segment apart from one
 * trailing slash, has no dot segment, and uses each placeholder as a whole
 * segment. tenant-config and the Admin apply the same rule on write.
 */
export function hostServedTemplateRefusal(
  template: string,
): string | undefined {
  const segments = templatePath(template).split('/').slice(1);
  const named =
    segments[segments.length - 1] === '' ? segments.slice(0, -1) : segments;
  const first = named[0] ?? '';
  if (first.length === 0 || ANY_PLACEHOLDER.test(first)) {
    return 'does not start its path with a literal segment';
  }
  if (named.some((segment) => segment.length === 0)) {
    return 'has an empty segment in its path';
  }
  if (named.some((segment) => DOT_SEGMENT.test(segment))) {
    return 'has a dot segment in its path';
  }
  if (
    named.some(
      (segment) => segment.includes('{') && !WHOLE_PLACEHOLDER.test(segment),
    )
  ) {
    return 'uses a placeholder as part of a path segment';
  }
  if (ANY_PLACEHOLDER.test(named[named.length - 1] ?? '')) {
    return 'does not end its path with a literal segment';
  }
  return undefined;
}

function unreservedValue(value: InputValue | undefined): string | undefined {
  if (typeof value === 'string') {
    return UNRESERVED.test(value) ? value : undefined;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value);
    return UNRESERVED.test(text) ? text : undefined;
  }
  return undefined;
}

function encode(value: InputValue | undefined): string {
  if (value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return encodeURIComponent(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return encodeURIComponent(String(value));
  }
  return value.map((item) => encodeURIComponent(item)).join(',');
}

const DOT_SEGMENT = /^(\.|%2e){1,2}$/i;

/** True when the path of an absolute URL has a `.` or `..` segment, which URL parsing would resolve away. */
function hasDotSegment(url: string): boolean {
  const afterScheme = url.slice(url.indexOf('://') + 3);
  const path = afterScheme.replace(/^[^/?#]*/, '').split(/[?#]/, 1)[0] ?? '';
  return path.split('/').some((segment) => DOT_SEGMENT.test(segment));
}

/**
 * Fills each `{name}` in the template with the encoded converted input, and
 * refuses a result that leaves `origin`, climbs out of the template's path
 * with a dot segment, contains a backslash, or exceeds the URL limit.
 */
export function buildSrc(
  template: string,
  inputs: Context['inputs'],
  origin: string,
  label: SrcLabel = 'iframe',
  options: BuildSrcOptions = {},
): string {
  const input = (name: string): InputValue | undefined =>
    Object.prototype.hasOwnProperty.call(inputs, name)
      ? inputs[name]
      : undefined;
  const pathEnd =
    options.hostServed === true
      ? template.length - template.replace(/^[^?#]*/, '').length
      : 0;
  const filled = template.replace(
    PLACEHOLDER,
    (_match, name: string, offset: number) => {
      if (offset >= pathEnd) {
        return encode(input(name));
      }
      const value = unreservedValue(input(name));
      if (value === undefined) {
        throw new LoaderError(
          'load-failed',
          `The ${label} src path input ${name} must be letters, digits, -, ., _ or ~ on a host-served entry`,
        );
      }
      return value;
    },
  );
  if (filled.includes('\\')) {
    throw new LoaderError(
      'load-failed',
      `The ${label} src contains a backslash, which URL parsing reads as a slash`,
    );
  }
  if (hasDotSegment(filled)) {
    throw new LoaderError(
      'load-failed',
      `The ${label} src has a dot segment in its path`,
    );
  }
  let url: URL;
  try {
    url = new URL(filled);
  } catch (error) {
    throw new LoaderError(
      'load-failed',
      `The ${label} src is not a valid URL`,
      {
        cause: error,
      },
    );
  }
  if (url.origin !== origin) {
    throw new LoaderError(
      'load-failed',
      `The ${label} src left its origin ${origin}`,
    );
  }
  if (url.href.length > SRC_MAX_LENGTH) {
    throw new LoaderError(
      'load-failed',
      `The ${label} src is longer than ${SRC_MAX_LENGTH} characters`,
    );
  }
  return url.href;
}
